@file:OptIn(ExperimentalJsExport::class)
@file:JsExport

package components

import game.GameAction
import game.BulletType
import game.ChassisType
import game.GameConfig
import game.GameState
import game.GameStatus
import game.gameStore
import react.CSSProperties
import react.ChildrenBuilder
import react.FC
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import js.reflect.unsafeCast
import web.cssom.ClassName

// 构建内联样式对象
private fun inlineStyle(widthPercent: Int, background: String): CSSProperties {
    val props: dynamic = js("({})")
    props.width = "$widthPercent%"
    props.background = background
    return props.unsafeCast<CSSProperties>()
}

// 技能按钮(带冷却遮罩)
private fun ChildrenBuilder.skillButton(key: String, label: String, cooldown: Double, maxCooldown: Double) {
    val ready = cooldown <= 0.0
    val percent = if (maxCooldown > 0.0) ((cooldown / maxCooldown) * 100.0).toInt().coerceIn(0, 100) else 0
    div {
        className = ClassName("skill-btn ${if (ready) "skill-ready" else ""}")
        span {
            className = ClassName("skill-key")
            +key
        }
        span {
            className = ClassName("skill-label")
            +label
        }
        if (!ready) {
            div {
                className = ClassName("skill-cooldown")
                style = inlineStyle(percent, "rgba(0,0,0,0.7)")
                +"${cooldown.toInt().coerceAtLeast(1)}"
            }
        }
    }
}

// 车库底盘卡片
private fun ChildrenBuilder.chassisCard(name: String, chassis: ChassisType, state: GameState) {
    val owned = chassis in state.ownedChassis
    val selected = chassis == state.selectedChassis
    val price = when (chassis) {
        ChassisType.LIGHT -> 0
        ChassisType.MEDIUM -> GameConfig.CHASSIS_MEDIUM_PRICE
        ChassisType.HEAVY -> GameConfig.CHASSIS_HEAVY_PRICE
    }
    val speedMult = when (chassis) {
        ChassisType.LIGHT -> GameConfig.LIGHT_SPEED_MULT
        ChassisType.MEDIUM -> GameConfig.MEDIUM_SPEED_MULT
        ChassisType.HEAVY -> GameConfig.HEAVY_SPEED_MULT
    }
    val hpMult = when (chassis) {
        ChassisType.LIGHT -> GameConfig.LIGHT_HP_MULT
        ChassisType.MEDIUM -> GameConfig.MEDIUM_HP_MULT
        ChassisType.HEAVY -> GameConfig.HEAVY_HP_MULT
    }
    val armor = when (chassis) {
        ChassisType.LIGHT -> GameConfig.LIGHT_ARMOR
        ChassisType.MEDIUM -> GameConfig.MEDIUM_ARMOR
        ChassisType.HEAVY -> GameConfig.HEAVY_ARMOR
    }
    div {
        className = ClassName("shop-card ${if (selected) "shop-selected" else ""}")
        h2 { +name }
        p { +"速度 ×$speedMult · 血量 ×$hpMult · 护甲 $armor%" }
        when {
            selected -> {
                span { className = ClassName("shop-state"); +"已装备" }
            }
            owned -> {
                button {
                    className = ClassName("btn-start btn-small")
                    onClick = { gameStore.dispatch(GameAction.SelectChassis(chassis)) }
                    +"装备"
                }
            }
            state.coins >= price -> {
                button {
                    className = ClassName("btn-start btn-small")
                    onClick = { gameStore.dispatch(GameAction.BuyChassis(chassis)) }
                    +"购买 $price💰"
                }
            }
            else -> {
                span { className = ClassName("shop-state shop-locked"); +"金币不足 ($price💰)" }
            }
        }
    }
}

// 商店武器卡片
private fun ChildrenBuilder.weaponCard(name: String, weapon: BulletType, price: Int, state: GameState) {
    val owned = weapon in state.ownedWeapons
    val selected = weapon == state.selectedWeapon
    div {
        className = ClassName("shop-card ${if (selected) "shop-selected" else ""}")
        h2 { +name }
        p { +weaponDesc(weapon) }
        when {
            selected -> {
                span { className = ClassName("shop-state"); +"已装备" }
            }
            owned -> {
                button {
                    className = ClassName("btn-start btn-small")
                    onClick = { gameStore.dispatch(GameAction.BuyWeapon(weapon)) }
                    +"装备"
                }
            }
            state.coins >= price -> {
                button {
                    className = ClassName("btn-start btn-small")
                    onClick = { gameStore.dispatch(GameAction.BuyWeapon(weapon)) }
                    +"购买 $price💰"
                }
            }
            else -> {
                span { className = ClassName("shop-state shop-locked"); +"金币不足 ($price💰)" }
            }
        }
    }
}

private fun weaponDesc(w: BulletType): String = when (w) {
    BulletType.STANDARD -> "均衡的常规炮弹"
    BulletType.LASER -> "高速穿透激光"
    BulletType.MISSILE -> "高伤范围爆炸"
    BulletType.HOMING -> "自动追踪敌人"
    BulletType.MELEE -> "近身范围电击"
    BulletType.SHOTGUN -> "多发弹片散射"
    BulletType.PLASMA -> "高伤等离子球"
    BulletType.GATLING -> "超快射速机炮"
    BulletType.EMP -> "范围电磁眩晕"
    BulletType.GRENADE -> "弧形弹道范围爆炸"
    BulletType.FLAME -> "持续燃烧火焰"
    BulletType.OBLIVION -> "💀 湮灭之核: 全屏秒杀 · 黑洞特效 · 毁天灭地"
}

// HUD:血量条、分数、波次、控制提示
val GameHud = FC("GameHud") {
    val state = useGameState()
    val hpPercent = (state.player.hp.toDouble() / state.player.maxHp.toDouble() * 100.0).toInt().coerceIn(0, 100)
    val energyPercent = (state.player.energy / state.player.maxEnergy * 100.0).toInt().coerceIn(0, 100)
    val enemiesAlive = state.enemies.count { it.alive }

    when (state.status) {
        GameStatus.MENU -> {
            div {
                className = ClassName("game-overlay")
                div {
                    className = ClassName("overlay-card")
                    h1 { +"🎮 坦克大战" }
                    p { +"WASD 移动 · 空格开火 · Shift 跳跃 · Q/E 俯仰视角" }
                    p { +"T=冲刺  Y=护盾  U=核弹,拾取道具获得增益!" }
                    p { className = ClassName("overlay-coins"); +"💰 金币: ${state.coins}" }
                    button {
                        className = ClassName("btn-start")
                        onClick = { gameStore.dispatch(GameAction.StartGame) }
                        +"开始游戏"
                    }
                    button {
                        className = ClassName("btn-menu")
                        onClick = { gameStore.dispatch(GameAction.OpenGarage) }
                        +"🚗 车库"
                    }
                    button {
                        className = ClassName("btn-menu")
                        onClick = { gameStore.dispatch(GameAction.OpenShop) }
                        +"🛒 商店"
                    }
                }
            }
        }

        GameStatus.GARAGE -> {
            div {
                className = ClassName("game-overlay")
                div {
                    className = ClassName("overlay-card overlay-wide")
                    h1 { +"🚗 车库" }
                    p { +"选择底盘:轻甲(快) / 中甲(均衡) / 重甲(厚)" }
                    p { className = ClassName("overlay-coins"); +"💰 金币: ${state.coins}" }

                    div {
                        className = ClassName("shop-grid")
                        chassisCard("轻甲", ChassisType.LIGHT, state)
                        chassisCard("中甲", ChassisType.MEDIUM, state)
                        chassisCard("重甲", ChassisType.HEAVY, state)
                    }

                    button {
                        className = ClassName("btn-start")
                        onClick = { gameStore.dispatch(GameAction.StartGame) }
                        +"出战!"
                    }
                    button {
                        className = ClassName("btn-menu")
                        onClick = { gameStore.dispatch(GameAction.ToMenu) }
                        +"返回菜单"
                    }
                }
            }
        }

        GameStatus.SHOP -> {
            div {
                className = ClassName("game-overlay")
                div {
                    className = ClassName("overlay-card overlay-wide")
                    h1 { +"🛒 军火商店" }
                    p { +"购买武器:多种枪械任选" }
                    p { className = ClassName("overlay-coins"); +"💰 金币: ${state.coins}" }

                    div {
                        className = ClassName("shop-grid")
                        weaponCard("标准炮弹", BulletType.STANDARD, 0, state)
                        weaponCard("霰弹", BulletType.SHOTGUN, GameConfig.WEAPON_SHOTGUN_PRICE, state)
                        weaponCard("激光", BulletType.LASER, GameConfig.WEAPON_LASER_PRICE, state)
                        weaponCard("加特林", BulletType.GATLING, GameConfig.WEAPON_GATLING_PRICE, state)
                        weaponCard("导弹", BulletType.MISSILE, GameConfig.WEAPON_MISSILE_PRICE, state)
                        weaponCard("等离子", BulletType.PLASMA, GameConfig.WEAPON_PLASMA_PRICE, state)
                        weaponCard("追踪弹", BulletType.HOMING, GameConfig.WEAPON_HOMING_PRICE, state)
                        weaponCard("EMP", BulletType.EMP, GameConfig.WEAPON_EMP_PRICE, state)
                        weaponCard("榴弹炮", BulletType.GRENADE, GameConfig.WEAPON_GRENADE_PRICE, state)
                        weaponCard("火焰炮", BulletType.FLAME, GameConfig.WEAPON_FLAME_PRICE, state)
                        weaponCard("近战电击", BulletType.MELEE, GameConfig.WEAPON_MELEE_PRICE, state)
                        // ===== 🚀 湮灭之核 =====
                        weaponCard("💀 湮灭之核", BulletType.OBLIVION, GameConfig.OBLIVION_PRICE, state)
                    }

                    button {
                        className = ClassName("btn-start")
                        onClick = { gameStore.dispatch(GameAction.StartGame) }
                        +"出战!"
                    }
                    button {
                        className = ClassName("btn-menu")
                        onClick = { gameStore.dispatch(GameAction.ToMenu) }
                        +"返回菜单"
                    }
                }
            }
        }

        GameStatus.PLAYING -> {
            div {
                className = ClassName("hud")

                div {
                    className = ClassName("hud-top")
                    span {
                        className = ClassName("hud-score")
                        +"得分: ${state.score}"
                    }
                    span {
                        className = ClassName("hud-wave")
                        +"第 ${state.wave + 1} 波"
                    }
                    span {
                        className = ClassName("hud-kills")
                        +"击杀: ${state.kills}"
                    }
                    span {
                        className = ClassName("hud-coins")
                        +"💰 ${state.coins}"
                    }
                    span {
                        className = ClassName("hud-weapon")
                        +"武器: ${weaponDesc(state.player.weapon)}"
                    }
                }

                // 暂停按钮
                button {
                    className = ClassName("btn-pause")
                    onClick = { gameStore.dispatch(GameAction.Pause) }
                    +"⏸"
                }

                div {
                    className = ClassName("hud-enemies")
                    span { +"剩余敌人: $enemiesAlive" }
                }

                div {
                    className = ClassName("hud-hint")
                    +"点击锁定鼠标 · 鼠标旋转视角 · Q/E 俯仰 · T/Y/U 技能 · P 暂停 · Esc 解锁"
                }

                // 血量条(蓝)
                div {
                    className = ClassName("hud-hpbar")
                    div {
                        className = ClassName("hud-hpbar-fill")
                        style = inlineStyle(hpPercent, "#3b82f6")
                    }
                    span {
                        className = ClassName("hud-hp-text")
                        +"${state.player.hp}/${state.player.maxHp}"
                    }
                }

                // 能量条(黄)
                div {
                    className = ClassName("hud-energybar")
                    div {
                        className = ClassName("hud-energybar-fill")
                        style = inlineStyle(energyPercent, "#eab308")
                    }
                    span {
                        className = ClassName("hud-hp-text")
                        +"⚡ ${state.player.energy.toInt()}/${state.player.maxEnergy.toInt()}"
                    }
                }

                // 技能栏
                div {
                    className = ClassName("hud-skills")
                    skillButton("T", "冲刺", state.player.dashCooldown, GameConfig.DASH_COOLDOWN)
                    skillButton("Y", "护盾", state.player.shieldCooldown, GameConfig.SHIELD_SKILL_COOLDOWN)
                    skillButton("U", "核弹", state.player.nukeCooldown, GameConfig.NUKE_COOLDOWN)
                }

                // 状态 buff 提示
                div {
                    className = ClassName("hud-buffs")
                    if (state.player.shieldTimer > 0.0) {
                        span {
                            className = ClassName("buff-tag buff-shield")
                            +"🛡 护盾"
                        }
                    }
                    if (state.player.speedTimer > 0.0) {
                        span {
                            className = ClassName("buff-tag buff-speed")
                            +"⚡ 加速"
                        }
                    }
                    if (state.player.rapidTimer > 0.0) {
                        span {
                            className = ClassName("buff-tag buff-rapid")
                            +"🔥 速射"
                        }
                    }
                    if (state.player.multiTimer > 0.0) {
                        span {
                            className = ClassName("buff-tag buff-multi")
                            +"💥 多重"
                        }
                    }
                }

                // 波次提示
                if (state.message.isNotBlank()) {
                    div {
                        className = ClassName("hud-message")
                        +state.message
                    }
                }
            }
        }

        GameStatus.PAUSED -> {
            div {
                className = ClassName("game-overlay")
                div {
                    className = ClassName("overlay-card")
                    h1 { +"⏸ 已暂停" }
                    p { +"第 ${state.wave + 1} 波 · 得分: ${state.score} · 金币: ${state.coins}" }
                    button {
                        className = ClassName("btn-start")
                        onClick = { gameStore.dispatch(GameAction.Resume) }
                        +"继续游戏"
                    }
                    button {
                        className = ClassName("btn-menu")
                        onClick = { gameStore.dispatch(GameAction.ToMenu) }
                        +"返回菜单"
                    }
                }
            }
        }

        GameStatus.GAME_OVER -> {
            div {
                className = ClassName("game-overlay")
                div {
                    className = ClassName("overlay-card")
                    h1 { +"💥 游戏结束" }
                    p { +"得分: ${state.score} · 击杀: ${state.kills}" }
                    button {
                        className = ClassName("btn-start")
                        onClick = { gameStore.dispatch(GameAction.Restart) }
                        +"重新开始"
                    }
                    button {
                        className = ClassName("btn-menu")
                        onClick = { gameStore.dispatch(GameAction.ToMenu) }
                        +"返回菜单"
                    }
                }
            }
        }

        GameStatus.VICTORY -> {
            div {
                className = ClassName("game-overlay")
                div {
                    className = ClassName("overlay-card")
                    h1 { +"🏆 胜利!" }
                    p { +"得分: ${state.score}" }
                    button {
                        className = ClassName("btn-start")
                        onClick = { gameStore.dispatch(GameAction.Restart) }
                        +"再来一局"
                    }
                }
            }
        }
    }
}

